import { useEffect } from "react";
import { useUser } from "src/atoms/user";
import { Color, Flex, Icon, Input, Text, VerticalDivider } from "src/elements";

import { Tooltip } from "src/components";
import {
  ResponseGetMerchflows_merchflowId_Details,
  RouteGetMerchflows_merchflowId_Details,
} from "@CommonApi/merchflows/_merchflowId_/details";
import { useLoaders } from "src/components/InlineLoader";
import { useMerchflowNotesModal } from "src/modals/MerchflowNotes/store/hooks";
import { useMerchflowHeader } from "./store/hooks";
import { isDev } from "src/utils/env";
import { RouteGetCategories_categoryId_ } from "@CommonApi/categories/_categoryId_";
import { RouteGetStores_storeId_ } from "@CommonApi/stores/_storeId_";
import { RouteGetProjects_projectId_ } from "src/omni-common/projects/_projectId_";
import { MerchantSwitcher } from "src/components/MerchantSwitcher/MerchantSwitcher";
import { getLoginToken } from "src/utils";
import { User } from "@CommonApi/auth/login";
import { decodeJwtPayload } from "src/utils/jwt";
import { useToasts } from "src/components/Toasts";

const InformationBlock = ({
  title,
  value,
  isLoading,
}: {
  title: string;
  value?: string | number | null;
  isLoading?: boolean;
}) => (
  <Flex column>
    <Text variant="small1" color={Color.primary}>
      {title}:
    </Text>

    <Text variant="h5" color={Color.primaryActive} isLoading={isLoading} isLoadingWidth="100%">
      {value}
    </Text>
  </Flex>
);

const MerchflowIdBlock = ({
  merchflowId,
  details,
}: {
  merchflowId: number;
  details: ResponseGetMerchflows_merchflowId_Details | null | undefined;
}) => {
  const { openMerchflowNotesModal: _openMerchflowNotesModal } = useMerchflowNotesModal();
  const { hasLoader } = useLoaders();
  const { saveNotes } = useMerchflowHeader();

  const isDetailsLoading = hasLoader(RouteGetMerchflows_merchflowId_Details);

  const openMerchflowNotesModal = () => {
    if (!details) return;

    _openMerchflowNotesModal({
      merchflowId,
      notes: details.notes || "",
      onSave: (notes) => saveNotes(notes),
    });
  };

  return (
    <Flex column>
      <Text variant="caption1" color={Color.primary}>
        Merchflow ID:
      </Text>

      <Flex gap="5px" align="center">
        <Text variant="body1" color={Color.primaryActive} testId="merchflowId">
          {merchflowId}
        </Text>

        {isDetailsLoading && <Icon name="spinner" size={14} />}

        {!isDetailsLoading && details && (
          <Flex onClick={openMerchflowNotesModal}>
            <Icon name="info" size={14} />
            <Tooltip>{details.notes} (Click to edit)</Tooltip>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

interface Props {
  merchflowId?: number;
  categoryId?: number;
  storeId?: number;
  projectId?: number;
  showProjects?: boolean;
  showFrogger?: boolean;
  s0UserTriggerId?: number;
  s1UserTriggerId?: number;
  basePogId?: number;
  storeCode?: string;
  storePogId?: number;
  templateId?: string | null;
  imagine?: "SHELVES";
}

export const MerchflowHeader = ({
  merchflowId,
  categoryId,
  storeId,
  projectId,
  showProjects,
  showFrogger = true,
  s0UserTriggerId,
  s1UserTriggerId,
  basePogId,
  storeCode,
  storePogId,
  templateId,
  imagine,
}: Props) => {
  const { user } = useUser();
  const {
    merchflowDetails,
    project,
    category,
    store,
    loadDetails,
    loadProject,
    loadCategory,
    loadStore,
    froggerVersion,
    setFroggerVersion,
  } = useMerchflowHeader();
  const { hasLoader } = useLoaders();

  const { toast } = useToasts();

  let isSsoUser: boolean;
  try {
    isSsoUser =
      (decodeJwtPayload(getLoginToken()!) as { user: User }).user.ssoProvider === "JUMPCLOUD";
  } catch (error) {
    toast({
      title: "Failed to initialize MerchflowHeader",
      error,
    });
    return;
  }
  useEffect(() => {
    if (merchflowId) {
      loadDetails(merchflowId);
    }
  }, [merchflowId]);

  useEffect(() => {
    if (projectId) {
      loadProject(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (categoryId) {
      loadCategory(categoryId);
    }
  }, [categoryId]);

  useEffect(() => {
    if (storeId) {
      loadStore(storeId);
    }
  }, [storeId]);

  return (
    <Flex justify="between" padding="10px" borderColor={Color.greenSmokeActive} borderRadius="3px">
      <Flex gap="15px" align="center">
        {!isSsoUser && <InformationBlock title="Retailer" value={user.merchantName} />}
        {isSsoUser && <MerchantSwitcher />}
        <VerticalDivider color={Color.greenSmokeActive} />

        {imagine === "SHELVES" && (
          <>
            <Text variant="h4" color={Color.primaryActive}>
              IMAGINE
            </Text>

            <Text variant="small1" color={Color.primary}>
              Shelves
            </Text>

            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}

        {categoryId && (
          <>
            <InformationBlock
              title="Category"
              value={category?.categoryCode}
              isLoading={hasLoader(RouteGetCategories_categoryId_)}
            />
            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}

        {storeId && (
          <>
            <InformationBlock
              title="Store"
              value={store?.code}
              isLoading={hasLoader(RouteGetStores_storeId_)}
            />
            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}

        {projectId && (
          <>
            <Flex align="center">
              <Text variant="h4" color={Color.primary} textTransform="uppercase">
                Pog Sets
              </Text>
            </Flex>

            <VerticalDivider color={Color.greenSmokeActive} />

            <InformationBlock title="Project ID" value={projectId} />

            <VerticalDivider color={Color.greenSmokeActive} />

            <InformationBlock
              title="Project Name"
              value={project?.name}
              isLoading={hasLoader(`GET_${RouteGetProjects_projectId_}`)}
            />
          </>
        )}

        {showProjects && (
          <Flex align="center">
            <Text variant="h4" color={Color.primary} textTransform="uppercase">
              Projects
            </Text>
          </Flex>
        )}

        {merchflowId && (
          <>
            <InformationBlock
              title="Category"
              value={merchflowDetails?.retailer_category_code}
              isLoading={
                merchflowDetails === undefined || hasLoader(RouteGetMerchflows_merchflowId_Details)
              }
            />
            <VerticalDivider color={Color.greenSmokeActive} />

            <MerchflowIdBlock merchflowId={merchflowId} details={merchflowDetails} />
            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}

        {s0UserTriggerId !== undefined && (
          <>
            <InformationBlock title="S0 ID" value={s0UserTriggerId} />

            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}

        {s1UserTriggerId !== undefined && (
          <>
            <InformationBlock title="S1 ID" value={s1UserTriggerId} />
            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}

        {templateId !== undefined && (
          <>
            <InformationBlock title="Template ID" value={templateId} />
            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}

        {basePogId && (
          <>
            <InformationBlock title="Base Planogram ID" value={basePogId} />
            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}

        {storeCode && (
          <>
            <InformationBlock title="Store Code" value={storeCode} />
            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}

        {storePogId && (
          <>
            <InformationBlock title="Store Planoram ID" value={storePogId} />
            <VerticalDivider color={Color.greenSmokeActive} />
          </>
        )}
      </Flex>

      {isDev() && showFrogger && (
        <Flex>
          <Input
            value={froggerVersion}
            setValue={setFroggerVersion}
            title="Frogger version"
            width="120px"
          />
        </Flex>
      )}
    </Flex>
  );
};
